import Vue from 'vue';
import VueRouter, { NavigationGuardNext, RouteConfig } from 'vue-router';
import { Route } from 'vue-router/types/router';
import Sessions from '@/views/Sessions/Sessions.vue';
import Launch from '@/views/Launch/Launch.vue';
import Login from '@/views/Login/Login.vue';
import Auth from '@/views/Auth/Auth.vue';
import AuthDevices from '@/views/AuthDevices/AuthDevices.vue';
import PluginOnboarding from '@/views/PluginOnboarding/PluginOnboarding.vue';
import EmailVerification from '@/views/EmailVerification/EmailVerification.vue';
import GoToPayment from '@/views/GoToPayment/GoToPayment.vue';
import DiscordCallback from '@/views/DiscordCallback/DiscordCallback.vue';
import TeamPage from '@/views/Team/TeamPage.vue';
import TeamReports from '@/views/Reports/TeamReports.vue';
import UserNotActivated from '@/views/UserNotActivated.vue';
import Page404 from '@/views/Page404.vue';
import ServerMaintenance from '@/views/ServerMaintenance.vue';
import Prototyping from '@/views/Prototyping/Prototyping.vue';
import AdminSurveys from '@/views/Admin/AdminSurveys/AdminSurveys.vue';
import AdminUsersManagement from '@/views/Admin/AdminUsersManagement/AdminUsersManagement.vue';
import AdminEditSoftwareOffers from '@/views/Admin/AdminEditSoftwareOffers/AdminEditSoftwareOffers.vue';
import AdminEditHardwareOffers from '@/views/Admin/AdminEditHardwareOffers/AdminEditHardwareOffers.vue';
import AdminEditAppOffers from '@/views/Admin/AdminEditAppOffers/AdminEditAppOffers.vue';
import AdminEditAnnouncements from '@/views/Admin/AdminEditAnnouncements/AdminEditAnnouncements.vue';
import AdminSessionsDashboard from '@/views/Admin/AdminSessionsDashboard/AdminSessionsDashboard.vue';
import AdminBulkEditNodeDefs from '@/views/Admin/AdminBulkEditNodeDefs/AdminBulkEditNodeDefs.vue';
import AdminManageToolTags from '@/views/Admin/AdminManageToolTags/AdminManageToolTags.vue';
import AdminManageKits from '@/views/Admin/AdminManageKits/AdminManageKits.vue';
import AdminFeaturedTemplates from '@/views/Admin/AdminFeaturedTemplates/AdminFeaturedTemplates.vue';
import store from '@/store';
import {
	shouldRedirectToHome,
	shouldRedirectToLogin,
	shouldRedirectFromUserNotActivated,
	shouldRedirectToUserNotActivated,
	shouldRedirectToEmailNotVerified,
	routeToHome,
} from '@/router/utils';
import { ROUTER } from '@/router/constants';
import _omit from 'lodash/omit';
import _isEqual from 'lodash/isEqual';
import _get from 'lodash/get';
import _isObject from 'lodash/isObject';
import { ClubKey } from '@run-diffusion/shared';
import {
	getProxyAdminToken,
	setCheckoutPromotionCodes,
	setCivitaiModelId,
	setCivitaiModelVersionId,
	setPlatformHomePreference,
} from '@/utils';
import SetupSession from '@/views/Sessions/SetupSession/SetupSession.vue';
import Runnits from '@/views/Runnits/Runnits.vue';
import RunnitsApp from '@/views/Runnits/RunnitsApp.vue';
import RunnitsHome from '@/views/Runnits/pages/RunnitsHome.vue';
import RunnitsLibraryPage from '@/views/Runnits/pages/RunnitLibraryPage.vue';
import RunnitAllToolsPage from '@/views/Runnits/pages/RunnitAllToolsPage.vue';
import RunnitBoard from '@/views/Runnits/RunnitBoard.vue';
import UserUsageReport from '@/views/Reports/UserUsageReport.vue';
import AppMetricsReport from '@/views/Reports/AppMetricsReport.vue';
import CompanyUsageReport from '@/views/Reports/CompanyUsageReport.vue';
import CompanyDashboard from '@/views/Team/CompanyDashboard/CompanyDashboard.vue';
import TeamDashboard from '@/views/Team/TeamDashboard/TeamDashboard.vue';
import TeamMemberDashboard from '@/views/Team/TeamMemberDashboard/TeamMemberDashboard.vue';
import MembersDirectoryPage from '@/views/Team/MembersDirectory/MembersDirectoryPage.vue';
import RunnitsSubscriptionsPage from '@/views/Runnits/pages/RunnitsSubscriptionsPage/RunnitsSubscriptionsPage.vue';
import ReferralPage from '@/views/Runnits/pages/ReferralPage/ReferralPage.vue';
import RunnitModels from '@/views/Runnits/pages/models/RunnitModels.vue';
import RunnitModelsListPage from '@/views/Runnits/pages/models/RunnitModelsListPage.vue';
import RunnitTrainModelOptions from '@/views/Runnits/pages/models/RunnitTrainModelOptions.vue';
import RunnitBoardsPage from '@/views/Runnits/pages/RunnitBoardsPage/RunnitBoardsPage.vue';
import RunnitTemplatesPage from '@/views/Runnits/pages/RunnitTemplatesPage/RunnitTemplatesPage.vue';
import RemixLandingPage from '@/views/Runnits/pages/RemixLandingPage.vue';
import RunnitAppLibrary from '@/views/Runnits/pages/RunnitAppLibrary.vue';
import HelpCenter from '@/views/HelpCenter/HelpCenter.vue';
import { TEAM_USER_ROLE_PERMISSION, COMPANY_USER_ROLE_PERMISSION } from '@run-diffusion/shared';
import WhatsNewPage from '@/views/Runnits/pages/WhatsNewPage/WhatsNewPage.vue';
import ManageUpdatesPage from '@/views/Runnits/pages/WhatsNewPage/ManageUpdatesPage.vue';
import RunnitReroute from '@/views/Runnits/pages/RunnitReroute.vue';
import ManageTeamRolesPage from '@/views/Team/CompanySettings/ManageTeamRolesPage.vue';
import ManageCompanyRolesPage from '@/views/Team/CompanySettings/ManageCompanyRolesPage.vue';
import CompanySettings from '@/views/Team/CompanySettings/CompanySettings.vue';
import CompanySettingsScimPage from '@/views/Team/CompanySettings/CompanySettingsScimPage.vue';
import PluginsPage from '@/views/Plugins/PluginsPage.vue';

Vue.use(VueRouter);

const routes: Array<RouteConfig> = [
	{
		path: '/',
		name: ROUTER.SESSIONS,
		component: Sessions,
		meta: {
			emailVerifiedAuthRequired: true,
			isSessionPage: true,
			isRunnitSideNavPage: true,
			teamPermissionRequired: TEAM_USER_ROLE_PERMISSION.USE_RD,
		},
	},
	{
		path: '/setup-session',
		name: ROUTER.SETUP_SESSION,
		component: SetupSession,
		meta: {
			emailVerifiedAuthRequired: true,
			isSessionPage: true,
			isRunnitSideNavPage: true,
			teamPermissionRequired: TEAM_USER_ROLE_PERMISSION.USE_RD,
		},
	},
	{
		path: '/another-session',
		name: ROUTER.ANOTHER_SESSION,
		component: Sessions,
		meta: {
			emailVerifiedAuthRequired: true,
			isSessionPage: true,
			isRunnitSideNavPage: true,
			teamPermissionRequired: TEAM_USER_ROLE_PERMISSION.USE_RD,
		},
	},
	{
		path: '/setup-another-session',
		name: ROUTER.SETUP_ANOTHER_SESSION,
		component: SetupSession,
		meta: {
			emailVerifiedAuthRequired: true,
			isSessionPage: true,
			isRunnitSideNavPage: true,
			teamPermissionRequired: TEAM_USER_ROLE_PERMISSION.USE_RD,
		},
	},
	{
		path: '/launch/:sessionId',
		name: ROUTER.LAUNCH,
		component: Launch,
		meta: {
			emailVerifiedAuthRequired: true,
			isSessionPage: true,
			isRunnitSideNavPage: true,
			teamPermissionRequired: TEAM_USER_ROLE_PERMISSION.USE_RD,
		},
	},
	{
		// Plugin surface for all signed-in users, gated at the route + nav by
		path: '/plugins',
		name: ROUTER.PLUGINS,
		component: PluginsPage,
		meta: {
			emailVerifiedAuthRequired: true,
			preventLaunchSession: true,
			isRunnitSideNavPage: true,
		},
	},
	{
		path: '/login',
		name: ROUTER.LOGIN,
		component: Login,
		meta: {
			isLoginPage: true,
		},
	},
	{
		path: '/signup',
		name: ROUTER.SIGNUP,
		component: Login,
		meta: {
			isLoginPage: true,
			forcedLoginRequired: true,
		},
	},
	{
		path: '/team-signup',
		name: ROUTER.TEAM_SIGNUP,
		component: Login,
		meta: {
			isLoginPage: true,
			forcedLoginRequired: true,
		},
	},
	{
		path: '/auth',
		name: ROUTER.AUTH,
		component: Auth,
		meta: {
			// Page handles its own sign-in redirect; standard auth guards
			// shouldn't bounce it elsewhere.
			isLoginPage: true,
		},
	},
	{
		path: '/auth/devices',
		name: ROUTER.AUTH_DEVICES,
		component: AuthDevices,
		meta: {
			// Render inside the runnit side-nav shell so the nav stays open when
			// arriving from the Plugins & API Integrations page. Access is handled
			// inside the page (it has its own signed-out / loading states).
			isRunnitSideNavPage: true,
			preventLaunchSession: true,
		},
	},
	{
		// Per-plugin onboarding walkthrough. Reached two ways: routed here
		// automatically after a successful device connect (see Auth.vue), and
		// re-opened anytime from each plugin's own Settings ("setup guide").
		path: '/plugins/:plugin/onboarding',
		name: ROUTER.PLUGIN_ONBOARDING,
		component: PluginOnboarding,
		meta: {
			// Plugin-facing surface, often hit in a fresh browser tab. It
			// handles its own (optional) sign-in display, so don't let the
			// global auth guards bounce it to /login.
			isLoginPage: true,
		},
	},
	{
		path: '/email-verification',
		name: ROUTER.EMAIL_VERIFICATION,
		component: EmailVerification,
		meta: {
			isLoginPage: true,
		},
	},
	{
		path: '/go-to-payment',
		name: 'GoToPayment',
		component: GoToPayment,
		meta: {
			emailVerifiedAuthRequired: true,
			preventLaunchSession: true,
		},
	},
	{
		path: '/discord-callback',
		name: 'DiscordCallback',
		component: DiscordCallback,
		meta: {
			emailVerifiedAuthRequired: true,
			preventLaunchSession: true,
		},
	},
	{
		path: '/team',
		component: TeamPage,
		children: [
			{
				path: '/',
				alias: [
					'/spaces', // old location
				],
				name: ROUTER.COMPANY_DASHBOARD,
				component: CompanyDashboard,
				meta: {
					emailVerifiedAuthRequired: true,
					preventLaunchSession: true,
					isRunnitSideNavPage: true,
				},
			},
			{
				path: 'company-settings',
				name: ROUTER.COMPANY_SETTINGS,
				component: CompanySettings,
				meta: {
					emailVerifiedAuthRequired: true,
					preventLaunchSession: true,
					isRunnitSideNavPage: true,
				},
			},
			{
				path: 'company-settings/team-roles',
				name: ROUTER.TEAM_ROLES,
				component: ManageTeamRolesPage,
				meta: {
					emailVerifiedAuthRequired: true,
					preventLaunchSession: true,
					isRunnitSideNavPage: true,
					companyPermissionRequired: COMPANY_USER_ROLE_PERMISSION.MANAGE_COMPANY_TEAM_ROLES,
				}
			},
			{
				path: 'company-settings/company-roles',
				name: ROUTER.COMPANY_ROLES,
				component: ManageCompanyRolesPage,
				meta: {
					emailVerifiedAuthRequired: true,
					preventLaunchSession: true,
					isRunnitSideNavPage: true,
					companyPermissionRequired: COMPANY_USER_ROLE_PERMISSION.MANAGE_COMPANY,
				}
			},
			{
				path: 'company-settings/sso-scim',
				name: ROUTER.COMPANY_SETTINGS_SSO_SCIM,
				component: CompanySettingsScimPage,
				meta: {
					emailVerifiedAuthRequired: true,
					preventLaunchSession: true,
					isRunnitSideNavPage: true,
					companyPermissionRequired: COMPANY_USER_ROLE_PERMISSION.MANAGE_COMPANY,
				}
			},
			// Static children stay ahead of ':teamId', which is a catch-all.
			{
				// No companyPermissionRequired: the members endpoint scopes its
				// response to the caller, and the page self-gates its actions.
				path: 'members',
				name: ROUTER.MEMBERS_DIRECTORY,
				component: MembersDirectoryPage,
				meta: {
					emailVerifiedAuthRequired: true,
					preventLaunchSession: true,
					isRunnitSideNavPage: true,
				},
			},
			{
				path: ':teamId',
				name: ROUTER.TEAM_DASHBOARD,
				component: TeamDashboard,
				meta: {
					emailVerifiedAuthRequired: true,
					preventLaunchSession: true,
					isRunnitSideNavPage: true,
				},
			},
			{
				path: ':teamId/team-member/:teamMemberId',
				name: ROUTER.TEAM_MEMBER_DASHBOARD,
				component: TeamMemberDashboard,
				meta: {
					emailVerifiedAuthRequired: true,
					preventLaunchSession: true,
					isRunnitSideNavPage: true,
				},
			},
			{
				path: 'reporting',
				name: 'TeamReporting',
				component: TeamReports,
				children: [
					{
						path: 'user-usage',
						name: ROUTER.USER_USAGE_REPORT,
						component: UserUsageReport,
						meta: {
							isRunnitSideNavPage: true,
							emailVerifiedAuthRequired: true,
							preventLaunchSession: true,
							companyPermissionRequired: COMPANY_USER_ROLE_PERMISSION.VIEW_COMPANY_REPORTS,
						},
					},
					{
						path: 'app-metrics',
						name: ROUTER.APP_METRICS_REPORT,
						component: AppMetricsReport,
						meta: {
							isRunnitSideNavPage: true,
							emailVerifiedAuthRequired: true,
							preventLaunchSession: true,
							companyPermissionRequired: COMPANY_USER_ROLE_PERMISSION.VIEW_COMPANY_REPORTS,
						},
					},
					{
						path: 'company-usage',
						name: ROUTER.COMPANY_USAGE_REPORT,
						component: CompanyUsageReport,
						meta: {
							isRunnitSideNavPage: true,
							emailVerifiedAuthRequired: true,
							preventLaunchSession: true,
							companyPermissionRequired: COMPANY_USER_ROLE_PERMISSION.VIEW_COMPANY_REPORTS,
						},
					},
				],
			},
		],
	},
	{
		path: '/proto',
		name: 'Prototyping',
		component: Prototyping,
		meta: {
			isInternalAdminPage: true,
			emailVerifiedAuthRequired: true,
			isRunnitSideNavPage: true,
		},
	},
	{
		path: '/help',
		name: ROUTER.HELP_CENTER,
		component: HelpCenter,
		meta: {
			emailVerifiedAuthRequired: true,
			preventLaunchSession: true,
			isRunnitSideNavPage: true,
		},
	},
	{
		path: '/runnit/remix',
		name: ROUTER.RUNNIT_REMIX,
		component: RemixLandingPage,
		meta: {
			preventLaunchSession: true,
			isLoginPage: true,
		},
	},
	{
		path: '/runnit',
		alias: [
			'/runnits', // old route alias
		],
		name: ROUTER.RUNNITS,
		component: Runnits,
		children: [
			{
				path: 'reroute',
				name: ROUTER.RUNNIT_REROUTE,
				component: RunnitReroute,
				meta: {
					emailVerifiedAuthRequired: true,
					preventLaunchSession: true,
				},
			},
			{
				path: 'home',
				name: ROUTER.RUNNITS_HOME,
				component: RunnitsHome,
				meta: {
					emailVerifiedAuthRequired: true,
					preventLaunchSession: true,
					isRunnitSideNavPage: true,
				},
			},
			{
				path: 'reward-program',
				name: ROUTER.RUNNITS_REFERRALS,
				component: ReferralPage,
				meta: {
					emailVerifiedAuthRequired: true,
					preventLaunchSession: true,
					isRunnitSideNavPage: true,
				},
			},
			{
				path: 'boards',
				name: ROUTER.RUNNIT_BOARDS_PAGE,
				component: RunnitBoardsPage,
				meta: {
					emailVerifiedAuthRequired: true,
					preventLaunchSession: true,
					isRunnitSideNavPage: true,
				},
			},
			{
				// Boards v2's list: canvas boards only, a Vue 3 bundle (web/board/src/list)
				// loaded on first visit. For accounts in the Boards v2 rollout (and
				// RunDiffusion admins): the page checks, and sends anyone else to v1's list.
				path: 'boards-v2',
				name: ROUTER.RUNNIT_BOARDS_V2_PAGE,
				component: () => import(/* webpackChunkName: "boards-list-page" */ '@/views/Boards/BoardsListPage.vue'),
				meta: {
					emailVerifiedAuthRequired: true,
					preventLaunchSession: true,
					isRunnitSideNavPage: true,
				},
			},
			{
				path: 'board-templates',
				name: ROUTER.RUNNIT_BOARD_TEMPLATES_PAGE,
				component: RunnitTemplatesPage,
				meta: {
					emailVerifiedAuthRequired: true,
					preventLaunchSession: true,
					isRunnitSideNavPage: true,
				},
			},
			{
				path: 'library',
				name: ROUTER.RUNNITS_LIBRARY,
				component: RunnitsLibraryPage,
				meta: {
					emailVerifiedAuthRequired: true,
					preventLaunchSession: true,
					isRunnitSideNavPage: true,
				},
			},
			{
				path: 'all-tools/:tag?',
				name: ROUTER.RUNNITS_ALL_TOOLS,
				component: RunnitAllToolsPage,
				meta: {
					emailVerifiedAuthRequired: true,
					preventLaunchSession: true,
					isRunnitSideNavPage: true,
				},
			},
			{
				path: 'subscriptions',
				name: ROUTER.RUNNITS_SUBSCRIPTIONS,
				component: RunnitsSubscriptionsPage,
				meta: {
					isLoginPage: true,
					preventLaunchSession: true,
					isRunnitSideNavPage: true,
				},
			},
			{
				path: 'models',
				name: ROUTER.RUNNITS_MODELS,
				component: RunnitModels,
				meta: {
					emailVerifiedAuthRequired: true,
					preventLaunchSession: true,
					isRunnitSideNavPage: true,
					teamPermissionRequired: TEAM_USER_ROLE_PERMISSION.VIEW_TRAIN,
				},
				children: [
					{
						path: 'list',
						name: ROUTER.RUNNITS_MODELS_LIST,
						component: RunnitModelsListPage,
						meta: {
							emailVerifiedAuthRequired: true,
							preventLaunchSession: true,
							isRunnitSideNavPage: true,
							teamPermissionRequired: TEAM_USER_ROLE_PERMISSION.VIEW_TRAIN,
						}
					},
					{
						path: 'train',
						name: ROUTER.RUNNITS_TRAIN_MODEL_OPTIONS,
						component: RunnitTrainModelOptions,
						meta: {
							emailVerifiedAuthRequired: true,
							preventLaunchSession: true,
							isRunnitSideNavPage: true,
							teamPermissionRequired: TEAM_USER_ROLE_PERMISSION.VIEW_TRAIN,
						}
					}
				]
			},
			{
				path: 'apps',
				name: ROUTER.RUNNITS_APP_LIBRARY,
				component: RunnitAppLibrary,
				meta: {
					emailVerifiedAuthRequired: true,
					preventLaunchSession: true,
					isRunnitSideNavPage: true,
				}
			},
			{
				path: 'whats-new',
				name: ROUTER.RUNNITS_WHATS_NEW,
				component: WhatsNewPage,
				meta: {
					emailVerifiedAuthRequired: true,
					preventLaunchSession: true,
					isRunnitSideNavPage: true,
				}
			},
			{
				path: 'manage-updates',
				name: ROUTER.MANAGE_UPDATES,
				component: ManageUpdatesPage,
				meta: {
					isInternalAdminPage: true,
					emailVerifiedAuthRequired: true,
					preventLaunchSession: true,
					isRunnitSideNavPage: true,
				}
			},
			{
				path: ':runnitId',
				component: RunnitsApp,
				children: [
					{
						path: '/',
						name: ROUTER.RUNNITS_BOARD,
						component: RunnitBoard,
						meta: {
							emailVerifiedAuthRequired: true,
							preventLaunchSession: true,
							isRunnitSideNavPage: true,
							hideAnnouncementsBanner: true,
						},
					},
				],
			},
		],
	},
	{
		path: '/admin/surveys',
		name: 'AdminSurveys',
		component: AdminSurveys,
		meta: {
			isInternalAdminPage: true,
			emailVerifiedAuthRequired: true,
			isRunnitSideNavPage: true,
		},
	},
	{
		path: '/admin/users-management',
		name: 'AdminUsersManagement',
		component: AdminUsersManagement,
		meta: {
			isInternalAdminPage: true,
			emailVerifiedAuthRequired: true,
			isRunnitSideNavPage: true,
		},
	},
	{
		path: '/admin/sessions-dashboard',
		name: 'AdminSessionsDashboard',
		component: AdminSessionsDashboard,
		meta: {
			isInternalAdminPage: true,
			emailVerifiedAuthRequired: true,
			isRunnitSideNavPage: true,
		},
	},
	{
		path: '/admin/edit-software-offers',
		name: 'AdminEditSoftwareOffers',
		component: AdminEditSoftwareOffers,
		meta: {
			isInternalAdminPage: true,
			emailVerifiedAuthRequired: true,
			isRunnitSideNavPage: true,
		},
	},
	{
		path: '/admin/edit-hardware-offers',
		name: 'AdminEditHardwareOffers',
		component: AdminEditHardwareOffers,
		meta: {
			isInternalAdminPage: true,
			emailVerifiedAuthRequired: true,
			isRunnitSideNavPage: true,
		},
	},
	{
		path: '/admin/edit-app-offers',
		name: 'AdminEditAppOffers',
		component: AdminEditAppOffers,
		meta: {
			isInternalAdminPage: true,
			emailVerifiedAuthRequired: true,
			isRunnitSideNavPage: true,
		},
	},
	{
		path: '/admin/edit-announcements',
		name: 'AdminEditAnnouncements',
		component: AdminEditAnnouncements,
		meta: {
			isInternalAdminPage: true,
			emailVerifiedAuthRequired: true,
			isRunnitSideNavPage: true,
		},
	},
	{
		path: '/admin/bulk-edit-runnit-tools',
		name: ROUTER.ADMIN_BULK_EDIT_NODE_DEFS,
		component: AdminBulkEditNodeDefs,
		meta: {
			isInternalAdminPage: true,
			emailVerifiedAuthRequired: true,
			isRunnitSideNavPage: true,
		},
	},
	{
		path: '/admin/manage-tool-tags',
		name: ROUTER.ADMIN_MANAGE_TOOL_TAGS,
		component: AdminManageToolTags,
		meta: {
			isInternalAdminPage: true,
			emailVerifiedAuthRequired: true,
			isRunnitSideNavPage: true,
		},
	},
	{
		path: '/admin/manage-kits',
		name: ROUTER.ADMIN_MANAGE_KITS,
		component: AdminManageKits,
		meta: {
			isInternalAdminPage: true,
			emailVerifiedAuthRequired: true,
			isRunnitSideNavPage: true,
		},
	},
	{
		path: '/admin/featured-templates',
		name: ROUTER.ADMIN_FEATURED_TEMPLATES,
		component: AdminFeaturedTemplates,
		meta: {
			isInternalAdminPage: true,
			emailVerifiedAuthRequired: true,
			isRunnitSideNavPage: true,
		},
	},
	{
		// Which teams and companies have Boards v2 (and turning them on and off)
		path: '/admin/boards-v2-rollout',
		name: ROUTER.ADMIN_BOARDS_V2_ROLLOUT,
		component: () => import(/* webpackChunkName: "admin-boards-v2-rollout" */ '@/views/Admin/AdminBoardsV2Rollout/AdminBoardsV2Rollout.vue'),
		meta: {
			isInternalAdminPage: true,
			emailVerifiedAuthRequired: true,
			isRunnitSideNavPage: true,
		},
	},
	{
		// A developer approving their feedback agent's sign-in (`npm run feedback -- login` opens it).
		// Before the feedback page's route, whose :number would take "connect"
		path: '/admin/feedback/connect',
		name: ROUTER.ADMIN_FEEDBACK_CONNECT,
		component: () => import(/* webpackChunkName: "admin-feedback-connect" */ '@/views/Admin/AdminFeedback/AdminFeedbackConnect.vue'),
		meta: {
			isInternalAdminPage: true,
			emailVerifiedAuthRequired: true,
			isRunnitSideNavPage: true,
		},
	},
	{
		// Feedback: read, approve and triage what testers report (/admin/feedback/12 opens #12)
		path: '/admin/feedback/:number?',
		name: ROUTER.ADMIN_FEEDBACK,
		component: () => import(/* webpackChunkName: "admin-feedback" */ '@/views/Admin/AdminFeedback/AdminFeedback.vue'),
		meta: {
			isInternalAdminPage: true,
			emailVerifiedAuthRequired: true,
			isRunnitSideNavPage: true,
		},
	},
	{
		path: '/user-not-activated',
		name: 'UserNotActivated',
		component: UserNotActivated,
	},
	// Boards v2 stress test: the demo board tiled past 4,500 images
	// (?images= for another size), held in memory so nothing is saved, with a
	// panel of live numbers and scripted benchmarks. A developer tool: local
	// development builds only, reached from the Dev chip; a production build
	// sends it to the boards list, admins included (IL-046). Listed before
	// /board/:boardId so "stress" is never read as a board id.
	process.env.NODE_ENV === 'production'
		? { path: '/board/stress', name: ROUTER.BOARD_STRESS, redirect: { name: ROUTER.RUNNIT_BOARDS_V2_PAGE } }
		: {
			path: '/board/stress',
			name: ROUTER.BOARD_STRESS,
			component: () => import(/* webpackChunkName: "board-page" */ '@/views/Board/BoardPage.vue'),
			props: { stress: true },
			meta: {
				emailVerifiedAuthRequired: true,
				preventLaunchSession: true,
				hideAnnouncementsBanner: true,
				pageColor: '#04030c',
				isFullscreenApp: true,
			},
		},
	{
		// The Boards v2 tour (IL-095): a practice board, in memory, with the tour
		// over it. A new account is sent here after its welcome survey (and back
		// here until it's done); anyone else on their first Boards v2 board, or
		// from a board's menu. ?reason=first-board|replay, ?board=<id>, ?back=<path>.
		path: '/welcome',
		name: ROUTER.BOARDS_TOUR,
		component: () => import(/* webpackChunkName: "boards-tour-page" */ '@/views/BoardsTour/BoardsTourPage.vue'),
		meta: {
			emailVerifiedAuthRequired: true,
			preventLaunchSession: true,
			hideAnnouncementsBanner: true,
			pageColor: '#04030c',
			isFullscreenApp: true,
		},
	},
	{
		// /board alone goes to the boards list. A static redirect, not a guard's:
		// a guard's redirect rejects the navigation that asked for /board, which
		// signing in with ?redirect=/board logged as an uncaught error (IL-050).
		path: '/board',
		redirect: { name: ROUTER.RUNNIT_BOARDS_V2_PAGE },
	},
	{
		// Boards v2: the canvas board. A separate Vue 3 + PixiJS bundle
		// (web/board), loaded on first visit so it never weighs on the rest of
		// the app.
		path: '/board/:boardId',
		name: ROUTER.BOARD,
		component: () => import(/* webpackChunkName: "board-page" */ '@/views/Board/BoardPage.vue'),
		meta: {
			emailVerifiedAuthRequired: true,
			preventLaunchSession: true,
			hideAnnouncementsBanner: true,
			pageColor: '#04030c',
			isFullscreenApp: true,
		},
	},
	{
		path: '/server-maintenance',
		name: 'ServerMaintenance',
		component: ServerMaintenance,
		meta: {
			hideAddFunds: true,
		},
	},
	{
		path: '/*',
		name: 'Page404',
		component: Page404,
	},
	// {
	// 	path: '/about',
	// 	name: 'About',
	// 	// route level code-splitting
	// 	// this generates a separate chunk (about.[hash].js) for this route
	// 	// which is lazy-loaded when the route is visited.
	// 	component: () => import(/* webpackChunkName: "about" */ '../views/About.vue'),
	// },
];

const router: VueRouter = new VueRouter({
	mode: 'history',
	routes,
});

router.beforeEach((to: Route, from: Route, next: NavigationGuardNext) => {
	if (shouldRedirectToLogin(to, store.state.user)) {
		// appQueryParamsData that should go to vuex no matter what
		const creatorsClubSignUp: boolean = !!_get(to.query, 'creatorsClubSignUp');
		const openManageMembership: boolean = !!_get(to.query, 'openManageMembership');
		const openManageTeamSubscription: boolean = !!_get(to.query, 'openManageTeamSubscription');
		if (
			creatorsClubSignUp ||
			openManageMembership ||
			openManageTeamSubscription
		) {
			store.commit('updateAppQueryParamsData', {
				...(creatorsClubSignUp && { creatorsClubSignUp }),
				...(openManageMembership && { openManageMembership }),
				...(openManageTeamSubscription && { openManageTeamSubscription }),
			});
		}

		// loginQueryParamsData that should go to vuex no matter what
		const autoLoginSsoProvider: string = _get(to.query, 'autoLoginSsoProvider') as string;
		if (autoLoginSsoProvider) {
			store.commit('updateLoginQueryParamsData', {
				...(autoLoginSsoProvider && { autoLoginSsoProvider }),
			});
		}

		// Process platformPreference query parameter
		const platformPreference: 'rd' | 'runnit' = (_get(to.query, 'platformPreference') || null) as 'rd' | 'runnit';
		if (platformPreference === 'rd') setPlatformHomePreference('RD')
		else if (platformPreference === 'runnit') setPlatformHomePreference('RUNNIT')

		// Remove incoming query parameters
		const toQuery: Record<string, string | (string | null)[]> = _omit(to.query || {}, [
			...(openManageMembership ? ['openManageMembership'] : []),
			...(openManageTeamSubscription ? ['openManageTeamSubscription'] : []),
			...(to.query.autoLoginSsoProvider && typeof to.query.autoLoginSsoProvider === 'string' ? ['autoLoginSsoProvider'] : []),
			...(platformPreference ? ['platformPreference'] : []),
		]);

		// Add redirect query param
		const updatedTo: any = {
			name: to.name,
			params: to.params,
			hash: to.hash,
			query: toQuery,
		};
		const toFullPath: string = router.resolve(updatedTo).href;
		if (toFullPath !== '/') {
			toQuery.redirect = toFullPath;
		}

		// Next route
		router.replace({
			name: ROUTER.LOGIN,
			query: toQuery,
		});
	} else if (shouldRedirectToEmailNotVerified(to, store.state.authUser)) {
		router.replace({
			name: ROUTER.EMAIL_VERIFICATION,
			query: {
				...(to.fullPath !== '/' && {
					redirect: to.fullPath,
				}),
			},
		});
	} else if (shouldRedirectFromUserNotActivated(to, store.state.user)) {
		router.replace({
			name: ROUTER.SESSIONS,
		});
	} else if (shouldRedirectToUserNotActivated(to, store.state.user)) {
		router.replace({
			name: 'UserNotActivated',
		});
	} else if (shouldRedirectToHome(to, store.state.user)) {
		routeToHome(router, store.state, to.query);
	} else if (to.name === 'GoToPayment' && getProxyAdminToken() && !confirm(`You are proxying another user. Are you sure you want to go to their payment screen?`)) {
		router.replace({
			name: ROUTER.SESSIONS,
		});
	}

	// Remove incoming query parameters
	let toQuery: Record<string, string | (string | null)[]> = to.query;
	if (toQuery.modelVersionId && typeof toQuery.modelVersionId === 'string') {
		setCivitaiModelVersionId(toQuery.modelVersionId);
		toQuery = _omit(toQuery, 'modelVersionId');
	}
	if (toQuery.modelId && typeof toQuery.modelId === 'string') {
		setCivitaiModelId(toQuery.modelId);
		toQuery = _omit(toQuery, 'modelId');
	}
	if (toQuery.checkoutPromotionCodes && typeof toQuery.checkoutPromotionCodes === 'string') {
		try {
			const checkoutPromotionCodes: Record<ClubKey, string> = JSON.parse(toQuery.checkoutPromotionCodes);
			if (_isObject(checkoutPromotionCodes)) {
				setCheckoutPromotionCodes(checkoutPromotionCodes);
			}
		} catch (e) {
			// continue on
		}
		toQuery = _omit(toQuery, 'checkoutPromotionCodes');
	}

	if (!_isEqual(to.query, toQuery)) {
		next({
			name: to.name,
			params: to.params,
			query: toQuery,
		});
	} else {
		next();
	}
});

// HubSpot SPA pageview tracking
router.afterEach((to: Route) => {
	// Push pageview to HubSpot tracking
	if (typeof window !== 'undefined') {
		const _hsq = (window as any)._hsq = (window as any)._hsq || [];
		_hsq.push(['setPath', to.fullPath]);
		_hsq.push(['trackPageView']);
	}
});

export default router;
